import { Input, InputRef, Spin } from 'antd';
import Modal from 'components/Modal';
import NumberPadV2 from 'components/NumberKeyBoard/Currency/NumberPadV2';
import Text from 'components/Text';
import { FEATURE_KEYS } from 'constants/Permission';
import { STAFF_TYPE } from 'features/appointment/services/constants';
import { aptUIActions } from 'features/appointment/services/reducers';
import { debounce, get, set } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import shopApis from 'services/shop/apis';
import { ROLE_CODE_PERMISSION } from 'services/shop/constants';
import shopSelectors from 'services/shop/selectors';
import { CheckPermissionPinResData } from 'services/shop/types/role';
import { IStaffItemData } from 'services/shop/types/staff';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
type IPermissionManagerProps = {};
type IPermissionManagerResolve = { skip: boolean, verifyData?: IStaffItemData | null };
type IPermissionManagerRef = {
  // if promise resolve with { skip: true }, means skip the permission verify
  // if promise resolve with { verifyData: IStaffItemData }, means the pin is correct and return the verify data
  verify: (featureKey: FEATURE_KEYS) => Promise<IPermissionManagerResolve | null>;
  close: () => void;
};
export const PermissionManagerRef = React.createRef<IPermissionManagerRef>();
const PermissionManagerContext = forwardRef<IPermissionManagerRef, IPermissionManagerProps>((props, ref) => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<InputRef>(null);
  const [visible, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [PIN_LENGTH] = useState(4);
  const permissionConfig = shopSelectors.data.permissionConfig();
  const callbackResolve = useRef<((resolve: IPermissionManagerResolve) => void) | undefined>(undefined);

  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | undefined>();
  const featureCodeRef = useRef<FEATURE_KEYS | undefined>(undefined);

  const allSetting = shopSelectors.data.allSetting();

  const showError = (text: string) => setError(text);
  const checkEmpty = async () => {
    if (Object.keys(ROLE_CODE_PERMISSION).length === 0) {
      permissionConfig.forEach(mod => {
        mod.features.forEach(feature => {
          const key = mod.moduleCode + '/' + feature.featureCode;
          set(ROLE_CODE_PERMISSION, key, feature.requirePin);
        });
      });
    }
  };

  const handleOpen: IPermissionManagerRef['verify'] = async (featureKey) => {
    await checkEmpty();
    if (!allSetting?.enableFeatureVerify) return new Promise((resolve) => {
      resolve(null);
    });

    const feature = ROLE_CODE_PERMISSION[featureKey];
    if (feature === undefined || feature === false) return new Promise((resolve) => {
      resolve({ skip: true, verifyData: undefined });
    });

    dispatch(aptUIActions.saveAptStaffDataPinCode(null));
    setPin('');
    setError(undefined);
    setOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 10);
    featureCodeRef.current = featureKey;
    return new Promise((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  const handleClose: IPermissionManagerRef['close'] = () => {
    setOpen(false);
  };

  const _onChange = (text: string) => {
    if (text.length > PIN_LENGTH) return;
    setPin(text);
  };


  const handle = useCallback(async (_pin: string) => {
    if (_pin.length < PIN_LENGTH) return;

    if (!featureCodeRef.current) return;

    const featureDetail = featureCodeRef.current.split('/') || [];
    if (!featureDetail.length) return;
    const [moduleCode, featureCode] = featureDetail;
    setLoading(true);
    try {
      const res: IResponseDataBody<CheckPermissionPinResData> = await shopApis.checkPermissionPin(_pin, moduleCode, featureCode);
      const resData = res.data.data;
      if (resData.authorized && resData.staff) {
        dispatch(aptUIActions.saveAptStaffDataPinCode(resData.staff || null));
        setOpen(false);
        callbackResolve.current?.({ skip: false, verifyData: resData.staff || null });
        return;
      }
      showError(resData.message || 'Password incorrect!');

    } catch (error) {
      showError('Password incorrect!');
    } finally {
      setLoading(false);
    }
  }, [PIN_LENGTH]);

  const _debounce = useCallback(debounce((_pin) => handle(_pin), 500), []);

  useEffect(() => {
    _debounce(pin);
  }, [pin]);

  useImperativeHandle(ref, () => ({
    verify: handleOpen,
    close: handleClose,
  }));

  return (
    <Modal
      modalTitle='ADMIN PASSWORD'
      hiddenOkButton
      v2
      noneBodyStyle
      width={'800px'}
      zIndex={99999}
      visible={visible} onClose={handleClose}>
      <Spin spinning={loading}>
        <InputUIV2>
          <Input
            ref={inputRef}
            name="input_password"
            value={pin}
            autoFocus
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
            inputMode='none'
            className={'password-input'}
            onChange={(e) => {
              _onChange(e.target.value);
            }}
            maxLength={PIN_LENGTH}
            autoComplete="off"
          />
        </InputUIV2>
        {error && (
          <Text textAlign="center" mt={0.5} style={{ color: '#f53f3f', fontSize: 18 }}>
            {error}
          </Text>
        )}
        <NumberPadV2
          value={pin} onChange={(val) => _onChange(val || '')}
          type='PIN-PASSWORD'
        />

      </Spin>
    </Modal>
  );
});

PermissionManagerContext.displayName = 'PermissionManagerContext';
const PermissionManager = {
  Portal: () => <PermissionManagerContext ref={PermissionManagerRef} />,
  verify: (featureKey: FEATURE_KEYS) => {
    return PermissionManagerRef.current?.verify(featureKey);
  },
  close: () => {
    PermissionManagerRef.current?.close();
  },
  PERMISSIONS: FEATURE_KEYS,
};

export default PermissionManager;

export const InputUIV2 = styled.div`
align-self: stretch;
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 16px 0;
  .password-input {
    -webkit-text-security: disc;
    text-security: disc;
    &.scanQR {
      input {
        text-align: center;
        padding-left: 50px;
      }
    }
  }
input {
  color: #D2464F;
  flex: 1;
  text-align: center;
  font-family: Poppins;
  font-size: 45px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}
`;